import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import step1 from "../../../assets/images/young-woman-studying-library.jpg";
import step2 from "../../../assets/images/young-student-working-assignment.webp";
import step3 from "../../../assets/images/study-group-african-people.jpg";
import step4 from "../../../assets/images/medium-shot-hard-working-student.jpg";

const stepIds = ["01", "02", "03", "04"];
const stepImages = [step1, step2, step3, step4];

const ArdrendPath = () => {
  const { t } = useTranslation();
  const steps = t("home.path.steps", { returnObjects: true }).map((step, i) => ({
    ...step,
    id: stepIds[i],
    img: stepImages[i],
  }));
  const [activeStep, setActiveStep] = useState(0);
  const stepRefs = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = stepRefs.current.indexOf(entry.target);
            if (index !== -1) {
              setActiveStep(index);
            }
          }
        });
      },
      {
        root: null,
        rootMargin: "-40% 0px -40% 0px", // Trigger when element is near the vertical center
        threshold: 0,
      }
    );

    const validRefs = stepRefs.current.filter(Boolean);
    validRefs.forEach((ref) => {
      observer.observe(ref);
    });

    return () => {
      validRefs.forEach((ref) => {
        observer.unobserve(ref);
      });
    };
  }, []);

  return (
    <section className="py-24 bg-gray-50 relative">
      <div className="max-w-[1560px] mx-auto px-6">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-end mb-16 lg:mb-24 gap-8">
          <div className="w-full lg:w-1/2">
            <div className="text-[11px] font-bold tracking-[0.15em] uppercase text-gray-400 mb-6 flex items-center gap-3">
              <span className="block w-8 h-[2px] bg-primary"></span>
              {t("home.path.kicker")}
            </div>
            <h2 className="text-[#0b0f19] text-3xl md:text-5xl font-bold">
              {t("home.path.title")}<span className="text-primary">.</span>
            </h2>
          </div>
          <div className="w-full lg:w-1/2 flex lg:justify-end">
            <p className="text-gray-500 text-lg max-w-[400px] leading-relaxed border-l border-primary/30 pl-6">
              {t("home.path.subtitle")}
            </p>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-16 lg:gap-24 relative">
          
          {/* Left Side: Sticky Image Container (Desktop Only) */}
          <div className="hidden lg:block lg:w-1/2 relative">
            <div className="sticky top-32 h-[600px] w-full rounded-[20px] overflow-hidden shadow-2xl border border-gray-200 bg-[#0b0f19]">
              {steps.map((step, idx) => (
                <div 
                  key={idx}
                  className={`absolute inset-0 transition-all duration-1000 ease-in-out ${
                    activeStep === idx 
                      ? 'opacity-100 scale-100 z-10' 
                      : 'opacity-0 scale-105 z-0'
                  }`}
                >
                  <img 
                    src={step.img} 
                    alt={step.title} 
                    className="w-full h-full object-cover opacity-80"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0b0f19] via-transparent to-transparent"></div>
                  
                  {/* Overlay text on image */}
                  <div className={`absolute bottom-10 left-10 text-white transition-all duration-700 delay-300 ${activeStep === idx ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'}`}>
                     <div className="flex items-end gap-4">
                        <span className="text-6xl font-black text-primary leading-none">{step.id}</span>
                        <h3 className="text-3xl font-bold pb-1">{step.title}</h3>
                     </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right Side: Scrollable Text Steps */}
          <div className="w-full lg:w-1/2 flex flex-col">
             {/* Add top padding so the first item can scroll to the middle smoothly */}
             <div className="hidden lg:block h-[15vh]"></div>
             
             {steps.map((step, idx) => (
              <div 
                key={idx} 
                ref={(el) => (stepRefs.current[idx] = el)}
                className={`flex flex-col justify-center min-h-[40vh] lg:min-h-[50vh] transition-all duration-700 ease-in-out py-12 ${
                  activeStep === idx ? 'opacity-100 lg:translate-x-0' : 'opacity-100 lg:opacity-20 lg:translate-x-8'
                }`}
              >
                {/* Mobile Image (Visible only on small screens) */}
                <div className="block lg:hidden w-full h-[300px] rounded-[15px] overflow-hidden mb-8 shadow-lg relative">
                  <img src={step.img} alt={step.title} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0b0f19]/90 to-transparent"></div>
                  <div className="absolute bottom-6 left-6 text-6xl font-black text-primary/80 leading-none">{step.id}</div>
                </div>

                <div className="flex flex-col">
                  <span className="hidden lg:block text-[100px] font-black text-gray-200 leading-none mb-4 transition-colors duration-500">
                    {step.id}
                  </span>
                  <h3 className={`text-3xl lg:text-4xl font-bold mb-6 transition-colors duration-500 ${activeStep === idx ? 'text-[#0b0f19]' : 'text-gray-400'}`}>
                    {step.title}
                  </h3>
                  <p className="text-gray-500 text-lg leading-relaxed max-w-lg">
                    {step.desc}
                  </p>
                  
                  {/* Visual Indicator Line */}
                  <div className={`h-[3px] mt-8 transition-all duration-500 ${activeStep === idx ? 'bg-primary w-24' : 'bg-gray-200 w-16'}`}></div>
                </div>
              </div>
            ))}
            
            {/* Add bottom padding so the last item can scroll past the middle */}
            <div className="hidden lg:block h-[30vh]"></div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default ArdrendPath;
